import React from "react";
import Table from '../base_table';
import type { TableRef } from '../base_table';
import type {
    ColumnDef,
    SortingState,
    ColumnFiltersState,
    ColumnOrderState,
    RowSelectionState,
    PaginationState,
    ExpandedState,
} from "@tanstack/react-table";
import { DeclarationPeriod } from "../../shared/Types";
import { Spinner } from "@nike/eds";
import {TableMessage, TableError} from "../base_table/table-components";

interface DeclarationPeriodTableProps {
    tableRef: React.Ref<TableRef>;
    columns: ColumnDef<DeclarationPeriod, any>[];
    data: DeclarationPeriod[];
    isLoading: boolean;
    error: any;
    tableTitle?: React.ReactNode;
    renderExpandedContent?: (declarationPeriod: DeclarationPeriod) => React.ReactNode;

    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    columnOrder: ColumnOrderState;
    rowSelection: RowSelectionState;
    expanded: ExpandedState;
    globalSearch?: string;
    columnFiltersSidePanelIsOpen: boolean;
    pageIndex: number;
    pageSize: number;
    totalCount?: number;

    onSortChange: (sorting: SortingState) => void;
    onColumnFiltersChange: (filters: ColumnFiltersState) => void;
    onColumnOrderChange: (order: ColumnOrderState) => void;
    onRowSelectionChange: (selection: RowSelectionState) => void;
    onPaginationChange: (pagination: PaginationState) => void;
    onGlobalFilterChange: (filter: string) => void;
    onExpandedChange: (expanded: ExpandedState) => void;
    onColumnFiltersSidePanelOpenChange: (isOpen: boolean) => void;
}

const TableSpinner = () => (
    <div className="noDataText">
        <Spinner className="eds-spacing--mt-8 eds-spacing--mb-8" size='large' />
    </div>
);

export const DeclarationPeriodsTable = React.memo(
    function DeclarationPeriodTable({
        tableRef,
        columns,
        data,
        isLoading,
        error,
        tableTitle,
        renderExpandedContent,
        sorting,
        columnFilters,
        columnOrder,
        rowSelection,
        expanded,
        globalSearch,
        columnFiltersSidePanelIsOpen,
        pageIndex,
        pageSize,
        totalCount,
        onSortChange,
        onColumnFiltersChange,
        onColumnOrderChange,
        onRowSelectionChange,
        onPaginationChange,
        onGlobalFilterChange,
        onExpandedChange,
        onColumnFiltersSidePanelOpenChange,
    }: DeclarationPeriodTableProps) {

        if (error) return (
            <TableError message={error.message || String(error)} />
        );

        return (
            <div style={{ height: "100%" }}>
                <Table
                    ref={tableRef}
                    className="declaration-periods-table"
                    columns={columns as any}
                    data={data}
                    tableTitle={tableTitle}
                    enableSorting
                    manualSorting
                    enablePagination
                    manualPagination
                    manualFiltering
                    enableRowSelection
                    enableMultiRowSelection={false}
                    enableGlobalFilter
                    enableGlobalFilterInSidePanel={false}
                    enableColumnFiltersSidePanel
                    enableColumnFilters={false}
                    getRowId={(row: DeclarationPeriod) => row.id ?? ""}
                    initialGlobalFilter={globalSearch}
                    globalFilterPlaceholder="Search by LRN, MRN..."
                    renderExpandedContent={renderExpandedContent}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onSortChange={onSortChange}
                    onColumnFiltersChange={onColumnFiltersChange}
                    onColumnOrderChange={onColumnOrderChange}
                    onRowSelectionChange={onRowSelectionChange}
                    onPaginationChange={onPaginationChange}
                    onGlobalFilterChange={onGlobalFilterChange}
                    onExpandedChange={onExpandedChange}
                    onColumnFiltersSidePanelOpenChange={onColumnFiltersSidePanelOpenChange}
                    noDataContent={isLoading ? <TableSpinner /> : <TableMessage msg="No declaration periods Found" />}
                    noDataText={isLoading ? "Loading declaration periods..." : "No declaration periods found."}
                    state={{
                        sorting,
                        columnFilters,
                        columnOrder,
                        rowSelection,
                        expanded,
                        columnFiltersSidePanelIsOpen,
                        columnVisibility: { declarationId: false, lrn: false, mrn: false },
                        pagination: { pageIndex, pageSize },
                    }}
                />
            </div>
        );
    }
);
